import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

// Resolves a display first name in priority order: `profiles.name`
// (DB of record), then `user_metadata.full_name` (auth metadata for
// OAuth signups). No email-local fallback — strings like
// "jmdarre.reg.1" read as usernames, not names. Returns undefined so
// consumers pick their own placeholder.
export function useFirstName(): string | undefined {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState<string | undefined>(() => {
    // Synchronous initial guess from auth metadata so the first
    // paint isn't blank while the DB roundtrip is in flight.
    const metaFull = user?.user_metadata?.full_name;
    return typeof metaFull === 'string' ? metaFull.trim().split(/\s+/)[0] || undefined : undefined;
  });

  useEffect(() => {
    if (!user?.id) {
      setFirstName(undefined);
      return;
    }

    let cancelled = false;
    (async () => {
      // Single-column read keeps this lightweight — we don't need
      // the rest of the profile row here.
      const { data, error } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled) return;

      const dbName = (!error && typeof data?.name === 'string') ? data.name.trim() : '';
      if (dbName) {
        setFirstName(dbName.split(/\s+/)[0]);
        return;
      }

      // DB had no name → fall back to auth metadata. Note: NO email
      // fallback — emails like "jmdarre.reg.1@…" don't read as names
      // and look worse than the consumer's placeholder ("you").
      const metaFull = user?.user_metadata?.full_name;
      const metaFirst = typeof metaFull === 'string' ? metaFull.trim().split(/\s+/)[0] : '';
      setFirstName(metaFirst || undefined);
    })();

    return () => { cancelled = true; };
  }, [user?.id, user?.user_metadata?.full_name]);

  return firstName;
}
